// actions.ts — the ChatAction set for Oriora Encore, DERIVED from THIS app's store
// (the live-music show ledger). Every action maps to a real store.ts / brain.ts call:
//   add    → store.createShow      (store supports create)
//   edit   → store.updateShow      (store supports update — used for star rating)
//   delete → store.deleteShow      (HITL-confirmed in Chat.tsx before it runs)
//   filter → store.listShows       (local text search over artists / venue / city)
//   summarize → brain.recapYear    (the app's EXISTING AI endpoint) with a LOCAL
//               computeSignals() fallback when AI is offline / unauthenticated / no funds.
// Nothing here invents a server route — it reuses only what store.ts + brain.ts export.

import { store, todayLocal, formatMoney, type ShowPatch, type NewShow } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "encore";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { recapYear, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

export interface FilterRow {
  id: string;
  seen_on: string;
  artists: string;
  venue: string;
  city: string;
  rating: number;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_show"; input: NewShow; label?: string }
  | { kind: "edit_show"; id: string; patch: ShowPatch; label?: string }
  | { kind: "delete_show"; id: string; label?: string }
  | { kind: "filter"; term: string; label?: string }
  | { kind: "summarize" }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;
  changed?: boolean;
  rows?: FilterRow[];
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_show": {
        const s = await store.createShow(a.input);
        return { ok: true, message: `Added ${s.artists.join(", ")}.`, changed: true };
      }

      case "edit_show":
        await store.updateShow(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the show"}.`, changed: true };

      case "delete_show":
        await store.deleteShow(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the show"}.`, changed: true };

      case "filter": {
        const all = await store.listShows({ limit: 50000 });
        const q = a.term.trim().toLowerCase();
        const matched = q
          ? all.filter(
              (s) =>
                s.artists.join(" ").toLowerCase().includes(q) ||
                s.venue.toLowerCase().includes(q) ||
                s.city.toLowerCase().includes(q),
            )
          : all;
        const rows: FilterRow[] = matched.slice(0, 50).map((s) => ({
          id: s.id,
          seen_on: s.seen_on,
          artists: s.artists.join(", "),
          venue: s.venue,
          city: s.city,
          rating: s.rating,
        }));
        // A bare "0 shows match." is a dead end (the Curio lesson, hit here live 2026-08-19:
        // a cost QUESTION routed to filter and matched nothing). Name what IS logged — local
        // data rendered locally — so the customer has somewhere to go next.
        let message: string;
        if (matched.length === 0) {
          const names = all.slice(0, 3).map((s) => s.artists.join(", ")).filter(Boolean).join(" · ");
          message = all.length === 0
            ? "Nothing logged yet — log your first show and I'll have something to search."
            : `No shows match. You have ${all.length} logged${names ? ` — ${names}${all.length > 3 ? ", …" : ""}` : ""}.`;
        } else {
          message = `${matched.length} ${matched.length === 1 ? "show" : "shows"} match.`;
        }
        return { ok: true, message, rows };
      }

      case "summarize": {
        const signals = await store.computeSignals(todayLocal());
        if (signals.overall.total_shows === 0) {
          return { ok: true, message: "No shows logged yet — add a few and I'll recap them." };
        }
        // Prefer the app's real AI recap (the ONE existing brain endpoint).
        try {
          const r = await recapYear(signals, "all");
          const text = r.recap.trim();
          if (text) return { ok: true, message: text };
        } catch {
          /* AI unavailable / not signed in / no funds → local summary below */
        }
        // Local fallback, from the on-device derived signals — always useful, no spend.
        const o = signals.overall;
        const parts: string[] = [
          `${o.total_shows} ${o.total_shows === 1 ? "show" : "shows"} across ${o.distinct_venues} ${
            o.distinct_venues === 1 ? "venue" : "venues"
          } in ${o.distinct_cities} ${o.distinct_cities === 1 ? "city" : "cities"}.`,
        ];
        const top = signals.top_artists[0];
        if (top && top.count >= 2) parts.push(`Most-seen: ${top.name} (${top.count}×).`);
        if (o.total_spend_cents !== null) parts.push(`Total logged spend ${formatMoney(o.total_spend_cents, signals.currency)}.`);
        if (signals.longest_gap) parts.push(`Longest gap between shows: ${signals.longest_gap.days} days.`);
        return { ok: true, message: parts.join(" ") };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my shows" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        // Save a single note or the whole conversation to the customer's OWN cloud (their GitHub/Drive),
        // distinct from backup_save (the whole shows collection). Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
